import { segmentTrack, segmentTrackOnce } from '@/hooks/analytics/useTracking';
import { McpConnectionStatus } from '@/models/User';

import { McpClientId } from './mcpClients';

export type McpSignpostSource = 'side_nav_mcp_widget' | 'global_header_mcp_widget';
export type McpSetupMethod = 'config_snippet_copied' | 'install_link_opened';
export type McpHelpType = 'full_install_guide' | 'token_fallback';
export type McpSetupDialogDismissMethod = 'close_button' | 'other';

export type McpSignpost = {
  source: McpSignpostSource;
  mcpConnectionStatus: McpConnectionStatus;
};

type McpSignpostWithNavState = McpSignpost & { isSideNavCollapsed?: boolean };

const signpostProps = ({ source, mcpConnectionStatus }: McpSignpost) => ({
  platform: 'website',
  source,
  mcp_connection_status: mcpConnectionStatus,
});

const navStateProps = (isSideNavCollapsed?: boolean) =>
  isSideNavCollapsed === undefined ? {} : { is_side_nav_collapsed: isSideNavCollapsed };

export function trackMcpSignpostShown({ source, mcpConnectionStatus, isSideNavCollapsed }: McpSignpostWithNavState) {
  segmentTrackOnce('Mcp Signpost Shown', {
    ...signpostProps({ source, mcpConnectionStatus }),
    ...navStateProps(isSideNavCollapsed),
  });
}

export function trackMcpSetupStarted({ source, mcpConnectionStatus, isSideNavCollapsed }: McpSignpostWithNavState) {
  segmentTrack('Mcp Setup Started', {
    ...signpostProps({ source, mcpConnectionStatus }),
    ...navStateProps(isSideNavCollapsed),
    event_schema_version: 2,
  });
}

export function trackMcpClientSetupAttempted({
  source,
  mcpConnectionStatus,
  mcpClient,
  setupMethod,
}: McpSignpost & { mcpClient: McpClientId; setupMethod: McpSetupMethod }): void {
  segmentTrack('Mcp Client Setup Attempted', {
    ...signpostProps({ source, mcpConnectionStatus }),
    event_schema_version: 2,
    event_type: 'product',
    mcp_client: mcpClient,
    setup_method: setupMethod,
  });
}

export function trackMcpSetupHelpOpened({
  source,
  mcpConnectionStatus,
  mcpClient,
  helpType,
}: McpSignpost & { mcpClient: McpClientId; helpType: McpHelpType }): void {
  segmentTrack('Mcp Setup Help Opened', {
    ...signpostProps({ source, mcpConnectionStatus }),
    event_schema_version: 2,
    mcp_client: mcpClient,
    help_type: helpType,
  });
}

export function trackMcpSetupDialogDismissed({
  source,
  mcpConnectionStatus,
  mcpClient,
  dismissMethod,
}: McpSignpost & { mcpClient: McpClientId; dismissMethod: McpSetupDialogDismissMethod }): void {
  segmentTrack('Mcp Setup Dialog Dismissed', {
    ...signpostProps({ source, mcpConnectionStatus }),
    mcp_client: mcpClient,
    popup_dismiss_method: dismissMethod,
  });
}

export function trackMcpSetupDialogFinished({
  source,
  mcpConnectionStatus,
  mcpClient,
}: McpSignpost & { mcpClient: McpClientId }): void {
  segmentTrack('Mcp Setup Dialog Finished', {
    ...signpostProps({ source, mcpConnectionStatus }),
    mcp_client: mcpClient,
  });
}
